// BFPE Intelligence Drawer — the defining cross-product UI for the Basketball
// layer. Every pick / leg / signal carries one: a pull-up panel showing exactly
// WHY a pick was made, which agents contributed, and the fair price vs the book
// line. Pure presentational component — screens build BfpeDrawerData from their
// real hook rows and pass it in. Markup ported verbatim from the CLAUDE DESIGN
// prototype (bb-overview.jsx BfpeDrawer + bb-gauntlet.jsx PersonaLeg drawer):
// `.bb-bfpe` toggle + `.bb-bfpe-panel` with up to five sections.
//
// Public-communication-safe by construction: OUTPUTS only (fair price, edge,
// agent labels + directional notes, CLV status) — never thresholds, module
// weights, or per-league/market win rates. Any missing field renders as a dash
// or is omitted; it never fabricates a number.

import { useState } from "react";
import { BIcon } from "./_bbkit.tsx";

export type DrawerTone = "edge" | "warn" | "neutral";

export interface AgentSignal {
  /** Agent label, e.g. "B-04 Fatigue", "Pace ledger", "The Pace Trigger". */
  readonly label: string;
  /** Short directional read, e.g. "ROAD B2B — fatigue 5.2 (ELEVATED)". */
  readonly note: string;
  /** Optional tone for the chip — `edge` greens, `warn` reds, default neutral. */
  readonly tone?: DrawerTone;
}

/** The five-section data contract. Every field optional past the verdict line;
 *  sections render only when their field is present. */
export interface BfpeDrawerData {
  // Section 1 — Verdict line (always rendered).
  readonly pick: string;                       // e.g. "Celtics First Half OVER 112.5"
  readonly fair: string;                       // pre-formatted fair price/odds
  readonly book: string;                       // pre-formatted book price/line
  readonly edge: string;                       // pre-formatted edge %
  readonly confidence: string;                 // HIGH / MEDIUM / LOW (or dash)
  // Section 2 — Why this pick (1-3 sentence narrative).
  readonly why?: string | null;
  // Section 3 — Agent signals.
  readonly agentsLabel?: string;
  readonly agents?: readonly AgentSignal[];
  // Section 4 — Fair price vs book (single projection line).
  readonly fairLineLabel?: string;
  readonly fairLine?: string | null;
  // Section 5 — Line & CLV (single status line).
  readonly clv?: string | null;
}

/** Inline expandable drawer. Collapsed = a single toggle button
 *  (`Open BFPE · <label>`). Expanded = the five-section panel below. */
export function BfpeDrawer({
  data,
  defaultLabel = "INTELLIGENCE",
  defaultOpen = false,
}: {
  data: BfpeDrawerData | null | undefined;
  defaultLabel?: string;
  defaultOpen?: boolean;
}): JSX.Element | null {
  const [open, setOpen] = useState(defaultOpen);
  if (!data) return null;
  return (
    <div className="bb-bfpe">
      <button type="button" className={`bb-bfpe-toggle ${open ? "open" : ""}`} onClick={() => setOpen((v) => !v)}>
        <BIcon name="chev" size={11} />
        <span className="chev">{open ? "Collapse" : "Open"} BFPE · {defaultLabel}</span>
      </button>
      {open ? (
        <div className="bb-bfpe-panel">
          <div className="bb-bfpe-verdict">
            <div className="pick">{data.pick}</div>
            <div className="kv">Fair<span className="v acc">{data.fair}</span></div>
            <div className="kv">Book<span className="v">{data.book}</span></div>
            <div className="kv">Edge<span className="v pos">{data.edge}</span></div>
            <div className="kv">Confidence<span className="v">{data.confidence}</span></div>
          </div>
          {data.why ? (
            <div className="bb-bfpe-section">
              <div className="h">Why this pick</div>
              <div className="p">{data.why}</div>
            </div>
          ) : null}
          {data.agents && data.agents.length > 0 ? (
            <div className="bb-bfpe-section">
              <div className="h">{data.agentsLabel ?? "Agent signals"}</div>
              <div className="bb-agent-list">
                {data.agents.map((a, i) => (
                  <div key={`${a.label}-${i}`} className={`bb-agent ${a.tone ?? ""}`}>
                    <div className="nm">{a.label}</div>
                    <div className="nt">{a.note}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          {data.fairLine ? (
            <div className="bb-bfpe-section">
              <div className="h">{data.fairLineLabel ?? "Fair price vs book"}</div>
              <div className="p">{data.fairLine}</div>
            </div>
          ) : null}
          {data.clv ? (
            <div className="bb-bfpe-section">
              <div className="h">Line &amp; CLV</div>
              <div className="p">{data.clv}</div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
